import { Box, Grid, Card, Link, styled } from '@mui/material'
import moment from 'moment'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppState } from 'appContext'
import { PasswordModal } from 'components/PasswordModal'
import { useAllApplications } from 'hooks'
import { useIsSmallScreen, useUrlGenerator } from 'packages/core'
import { PaddedDiv } from 'components/ComputerSessionRequest/SessionRequestTile'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import { Avatar } from 'packages/eid-ui'
import { getFullImageUrl } from 'utils'
const smallScreenWidth = '1022px'

export interface CredentialCheckoutTileProps {
    item: any
    parent: any
}

const useStyles = makeStyles((theme: any) => ({
    requestmaincontainer: {
        borderRadius: '0.8rem',
        boxShadow: '0 0.4rem 1.6rem 0 rgba(0, 0, 0, 0.05)',
        border: 'solid 0.1rem rgba(0, 0, 0, 0.11)',
        background: theme?.palette?.background?.paper,
        marginTop: '1.7rem',
        paddingBottom: '2rem',
        overflow: 'hidden',
    },

    requestHeadertitle: {
        fontSize: '1.4rem',
        fontWeight: 600,
        marginTop: '0.5rem',
        marginBottom: '0.5rem',
    },
    requestHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: 'solid 0.1rem rgba(0, 0, 0, 0.11)',
        padding: '0.4rem 2rem',
        backgroundImage:
            'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%)',
        backgroundSize: '0.8rem 0.8rem',
    },
    requestHeadertitlebutton: {
        '& button': {
            fontSize: '1.1rem',
            color: theme?.palette?.common?.white,
            background: theme?.palette?.secondary?.main,
            borderRadius: '0.5rem',
            padding: '0.1rem 0.4rem 0.1rem',
            textTransform: 'capitalize',
            '&:hover': {
                background: theme?.palette?.secondary?.main,
            },
        },
    },
    requestitemshow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '0.5rem 2rem',
        marginTop: '0.7rem',
        [`@media (max-width:${smallScreenWidth})`]: {
            flexDirection: 'column',
            alignItems: 'start',
            gap: '1.6rem',
        },
    },
    requestleftshow: {
        // TODO: Commenting until we get new design
        // width: '25%',
    },
    requestitemtitle: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#9b9b9b',
        margin: '0rem',
        textTransform: 'uppercase',
        [`@media (max-width:${smallScreenWidth})`]: {
            fontWeight: '600',
        },
    },
    requestitemheading: {
        fontSize: '1.4rem',
        fontWeight: 500,
        margin: '0rem',
    },
    requestitemheadingLink: {
        '& a': {
            color: `${theme?.palette?.primary?.main} !important`,
            textDecoration: 'none !important',
        },
    },
    requestitemtime: {
        fontSize: '1.4rem',
        fontWeight: 500,
        color: theme?.palette?.common?.black,
        margin: '0rem',
        '& button': {
            fontSize: '1.1rem',
            borderRadius: '0.5rem',
            padding: '0.1rem 0.4rem 0.1rem',
            textTransform: 'capitalize',
            '&:hover': {
                background: theme?.palette?.secondary?.main,
            },
        },
    },
    checkinbutton: {
        fontSize: '1.1rem',
        color: theme?.palette?.common?.white,
        background: theme?.palette?.primary?.main,
        borderRadius: '0.5rem',
        '&:hover': {
            background: theme?.palette?.primary?.main,
        },
    },
    avtarsection: {
        display: 'flex',
        alignItems: 'center',
        ' & > div': {
            height: '2rem',
            width: '2rem',
        },
    },
    avtarname: {
        marginLeft: '0.5rem',
    },
    requestconnectbtn: {
        ' & button': {
            padding: '0',
            ' & div': {
                maxWidth: 'auto !important',
            },
        },
    },
}))

export const CredentialCheckoutTile: FC<CredentialCheckoutTileProps> = (
    props,
) => {
    const { item } = props
    const [{ masterPassword }]: any = useAppState()
    const [open, setOpen] = useState('')
    const classes = useStyles()
    const { t } = useTranslation()
    const { getMyTasksUrl } = useUrlGenerator(useAllApplications)
    const isSmallScreen = useIsSmallScreen()
    const myTasksBusinessRequestUrl = getMyTasksUrl(
        `/myRequests/businessRequests?id=${item.businessRequestId}`,
    )
    const myTasksBusinessRequestItemUrl = getMyTasksUrl(
        `/myRequests/businessRequestItems?id=${item.businessRequestItemId}`,
    )

    return (
        <>
            {!masterPassword && (
                <PasswordModal
                    showDefaultContent={false}
                    open={open}
                    setOpen={setOpen}
                />
            )}

            <PaddedDiv isSmallScreen={isSmallScreen}>
                <Box
                    sx={{
                        borderImageSource:
                            'linear-gradient(to right, rgba(1, 174, 143, 0), #04ab936e 7%, #2c83bd47 91%, rgba(48, 127, 193, 0))',
                        borderImageSlice: '1',
                        borderBottomWidth: '1px',
                        borderStyle: 'solid',
                        borderTopWidth: '0',
                        overflow: 'hidden',
                    }}
                >
                    <Box className={classes.requestmaincontainer}>
                        <Box className={classes.requestHeader}>
                            <Box
                                component="h2"
                                className={classes.requestHeadertitle}
                            >
                                {item.credentialFriendlyName
                                    ? item.credentialFriendlyName
                                    : '---'}
                            </Box>
                        </Box>
                        <Box className={classes.requestitemshow}>
                            <Box className={classes.requestleftshow}>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtitle}
                                >
                                    {t('Common_BusinessRequest')}
                                </Box>
                                <Box
                                    component="h2"
                                    className={classNames(
                                        classes.requestitemheading,
                                        {
                                            [classes.requestitemheadingLink]:
                                                item?.businessRequestId &&
                                                item.businessRequestName,
                                        },
                                    )}
                                >
                                    {item?.businessRequestId &&
                                    item.businessRequestName ? (
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href={myTasksBusinessRequestUrl}
                                        >
                                            {item.businessRequestName
                                                ? item.businessRequestName
                                                : '---'}
                                        </a>
                                    ) : item.businessRequestName ? (
                                        item.businessRequestName
                                    ) : (
                                        '---'
                                    )}
                                </Box>
                            </Box>
                            <Box className={classes.requestleftshow}>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtitle}
                                >
                                    {t('Common_BusinessRequestItem')}
                                </Box>
                                <Box
                                    component="h2"
                                    className={classNames(
                                        classes.requestitemheading,
                                        {
                                            [classes.requestitemheadingLink]:
                                                item?.businessRequestId &&
                                                item.businessRequestItemName,
                                        },
                                    )}
                                >
                                    {item?.businessRequestItemId &&
                                    item.businessRequestItemName ? (
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href={myTasksBusinessRequestItemUrl}
                                        >
                                            {item.businessRequestItemName
                                                ? item.businessRequestItemName
                                                : '---'}
                                        </a>
                                    ) : item.businessRequestItemName ? (
                                        item.businessRequestItemName
                                    ) : (
                                        '---'
                                    )}
                                </Box>
                            </Box>
                            <Box className={classes.requestleftshow}>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtitle}
                                >
                                    {t('Common_RequestedBy')}
                                </Box>
                                <Box className={classes.avtarsection}>
                                    <Avatar
                                        size="small"
                                        src={getFullImageUrl(
                                            item.requestedByPersonImageThumbUrl,
                                        )}
                                    />
                                    <span className={classes.avtarname}>
                                        {item.requestorFriendlyName
                                            ? item.requestorFriendlyName
                                            : '---'}
                                    </span>
                                </Box>
                            </Box>
                        </Box>

                        <Box className={classes.requestitemshow}>
                            <Box className={classes.requestleftshow}>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtitle}
                                >
                                    {t('Common_StartTime')}
                                </Box>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtime}
                                >
                                    {item.startTimeUtc
                                        ? moment
                                              .utc(item.startTimeUtc)
                                              .local()
                                              .format('L LT')
                                        : '---'}
                                </Box>
                            </Box>
                            <Box className={classes.requestleftshow}>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtitle}
                                >
                                    {t('Common_EndTime')}
                                </Box>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtime}
                                >
                                    {item.endTimeUtc
                                        ? moment
                                              .utc(item.endTimeUtc)
                                              .local()
                                              .format('L LT')
                                        : '---'}
                                </Box>
                            </Box>

                            <Box>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtitle}
                                >
                                    {t('Common_ActualCheckOutTime')}
                                </Box>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtime}
                                >
                                    {item.userCheckedOutTimeUtc
                                        ? moment
                                              .utc(item.userCheckedOutTimeUtc)
                                              .local()
                                              .format('L LT')
                                        : '---'}
                                </Box>
                            </Box>
                            <Box className={classes.requestleftshow}>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtitle}
                                >
                                    {t('Common_ActualCheckInTime')}
                                </Box>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtime}
                                >
                                    {item.userCheckedInTimeUtc
                                        ? moment
                                              .utc(item.userCheckedInTimeUtc)
                                              .local()
                                              .format('L LT')
                                        : '---'}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </PaddedDiv>
        </>
    )
}

export default CredentialCheckoutTile
